import { HistoryMessage, Message } from '@/components/ui/chat-message';
import type { ChatMeta } from '@/lib/chat-protocol';
import { $customerAuth, authedFetch, decodeToken, logout } from '@/lib/auth';
import { Customer } from '@/lib/customer';
import { getCustomer, getSettings } from '@/lib/hec';
import { chatEngine } from '@/lib/chat-engine';
import { getShortID } from '@/router';
import { persistentAtom } from '@nanostores/persistent';
import { atom, map, onMount, task } from 'nanostores';
import { $inRNWebView } from './embed';
import negotiateAsset from './utils';

const $hasSeenOnboarding = persistentAtom<Record<string, boolean>>(
  'has-seen-onboarding',
  {},
  {
    encode: (v) => JSON.stringify(v),
    decode: (v) => {
      try {
        return JSON.parse(v);
      } catch {
        return {};
      }
    },
  },
);

export function setOnboardingSeen(): void {
  const shortID = getShortID();
  const sub = shortID ? decodeToken(shortID)?.sub : null;
  if (sub) {
    $hasSeenOnboarding.set({ ...$hasSeenOnboarding.get(), [sub]: true });
  }
}

export function getOnboardingSeen(): boolean {
  const shortID = getShortID();
  const sub = shortID ? decodeToken(shortID)?.sub : null;
  if (!sub) return true; // no token => don't show onboarding
  return $hasSeenOnboarding.get()?.[sub] ?? false;
}
export const $feedback = map<Record<string, boolean>>();

/** assets live here: https://admin.appsite.de/bf005ca8/manage/assets?tab=18 */
// key is settings.subdomain
const configurationTexts = {
  club: {
    originTitle: 'Club',
    originDescription:
      'Diese Inhalte stammen direkt aus Deinem Fitnessclub – von den Menschen, die Dich vor Ort betreuen, begleiten und motivieren. Das Team teilt hier praxisnahe Tipps, Empfehlungen und Hinweise aus dem Trainingsalltag, abgestimmt auf die Angebote und Schwerpunkte im Studio.  Ob saisonale Trainingstipps, Empfehlungen zu bestimmten Geräten oder Aktionen im Club: Du bekommst persönliche Inhalte, die genau auf Dein Umfeld und Deinen Trainingsalltag zugeschnitten sind.',
    originLogo: 'Club',
    originUrl: null,
  },

  ExpertenAllianz: {
    originTitle: 'Experten Allianz für Gesundheit',
    originDescription:
      'Die Expertenallianz für Gesundheit ist ein Zusammenschluss führender Köpfe aus Medizin, Wissenschaft, Sport, Therapie und Prävention. Ziel ist es, Gesundheitskompetenz in der Bevölkerung zu stärken – durch Aufklärung, Wissenstransfer und praxisnahe Empfehlungen.  Inhalte dieser Quelle basieren auf dem fachlichen Know-how namhafter Institutionen und Expert/innen. Sie liefern fundierte Impulse rund um Bewegung, Ernährung, Stressmanagement und Prävention – unabhängig, wissenschaftlich fundiert und verständlich aufbereitet.',
    originLogo: 'https://datamanager.entrecode.de/t/bf005ca8/RP8jhREQR2meXpw421HXJQ/400',
    originUrl: 'www.expertenallianz-gesundheit.de',
  },
  dsbonecontentb2c: {
    originTitle: 'MY COACH Experts',
    originDescription:
      'Hinter MY COACH Experts stehen erfahrene Autor/innen und Fachleute aus den Bereichen Training, Ernährung, Gesundheit, Motivation und Regeneration. Jedes Thema wird von Expert/innen aufbereitet, die wissen, worauf es ankommt – ob Sportwissenschaftler/in, Ernährungsexpert/in, Mental Coach oder Trainer/in aus der Praxis.  Du bekommst fundiertes Wissen, klar erklärt und direkt anwendbar – damit du deinen Körper und dein Training besser verstehst und gezielt vorankommst. MY COACH Experts liefert dir die richtigen Impulse – für jedes Ziel und jede Trainingsphase.',
    originLogo: 'https://datamanager.entrecode.de/t/bf005ca8/qhpEvDhfTBiBiDt_9F_EDA/400',
    originUrl: null,
  },

  mygymcontent: {
    originTitle: 'MYGYM',
    originDescription: 'MYGYM Inhalte',
    originLogo: null,
    originUrl: null,
  },

  mycommitment: {
    originTitle: 'Commitment',
    originDescription: 'Inhalte des Commitment-Center',
    originLogo: null,
    originUrl: null,
  },
};

declare type SourceKey = keyof typeof configurationTexts;

export function getLogo(document: Document, settings: any) {
  const { source } = document;

  if (source in configurationTexts) {
    return configurationTexts[source].originLogo;
  }
  return negotiateAsset(settings?.favicon || settings?.appLogo);
}

const apiURL = import.meta.env.VITE_API_URL;

export declare interface Document {
  id: string;
  title: string;
  type: 'article';
  thumbnailUrl?: string;
  source: SourceKey;
}

export declare interface Deeplink {
  deeplink: string;
  label: string;
}

function processHistory(chatHistory: HistoryMessage[], customer: Customer | null) {
  if (!chatHistory.length) {
    return [
      {
        id: '2',
        role: 'assistant',
        content: `Hallo${
          customer?.personalData?.name ? ` ${customer?.personalData?.name}` : ''
        }! Ich bin **MYCOACH** – dein smarter Assistent für Fitness & Clubleben. Ich helfe dir dabei, deine Ziele zu erreichen, beantworte deine Fragen zu Fitness und Gesundheit und buche jederzeit Kurse, Termine und ADD-ONs für dich. Und das Beste: **Du wirst immer stärker, ich immer smarter!** Wie kann ich dir heute helfen?`,
        createdAt: new Date(),
      },
    ];
  }
  const processed: Message[] = [];
  chatHistory.forEach((msg: HistoryMessage) => {
    // the backend packs the actions with the proceeding message
    // in the frontend, we treat an action as a separate message
    if (msg.action) {
      processed.push({
        id: String(new Date(msg.timestamp).getTime()),
        run_id: msg.run_id,
        createdAt: new Date(msg.timestamp),
        role: 'assistant',
        content: msg.action.content,
        documents: [],
        deeplinks: [],
        action: msg.action,
      });
      delete msg.action;
    }
    processed.push({
      role: msg.type === 'ai' ? 'assistant' : 'user',
      run_id: msg.run_id,
      content: msg.content,
      action: msg.action,
      createdAt: new Date(msg.timestamp),
      documents: msg.type === 'ai' ? msg.documents : [],
      deeplinks: msg.type === 'ai' ? msg.deeplinks : [],
      history: true,
      id: String(new Date(msg.timestamp).getTime()),
    });
  });
  return processed;
}

declare type Settings = any;

export const $isLoading = atom<boolean>(false);
export const $input = atom<string>('');
export const $initialMessage = atom<string | undefined>(undefined);
export const $customer = atom<Customer | null>(null);
export const $messages = atom<Message[]>([]);
export const $settings = atom<Settings>({});

// Multi-chat state. `$messages` holds the ACTIVE chat's messages (the backend
// serves each chat's history via `current_chat`, so we don't cache per chat).
// `$chats` is the sidebar list; `$activeChatId` is the currently open chat.
export const $chats = atom<ChatMeta[]>([]);
export const $activeChatId = atom<string | null>(null);
// true while a switch_chat is in flight (awaiting the current_chat reply)
export const $loadingChat = atom<boolean>(false);

export const setActiveChat = (id: string | null) => $activeChatId.set(id);
export const setLoadingChat = (v: boolean) => $loadingChat.set(v);

/** Insert or update a chat in the sidebar list (newest stays on top). */
export const upsertChat = (meta: ChatMeta) => {
  const list = $chats.get();
  const idx = list.findIndex((c) => c.chat_id === meta.chat_id);
  if (idx >= 0) {
    $chats.set(list.map((c, i) => (i === idx ? { ...c, ...meta } : c)));
  } else {
    $chats.set([meta, ...list]);
  }
};

/** Replace the active chat's messages from a backend history payload. */
export const setChatHistory = (history: HistoryMessage[]) => {
  $messages.set(processHistory(history, $customer.get()));
};

/** Load the customer's chats into `$chats` for the sidebar (newest first). */
export async function loadChatList(shortID: string): Promise<void> {
  const decoded = decodeToken(shortID);
  if (!decoded) return;
  const customer_id = `${decoded.data.backendID}$${decoded.data.customerID}`;
  const res = await authedFetch(shortID, `${apiURL}/chats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer_id }),
  });
  if (!res.ok) {
    if (res.status === 403) {
      logout(shortID);
    }
    throw new Error('failed to load chats');
  }
  const chats: ChatMeta[] = await res.json();
  $chats.set(chats);
}

/** Clear all chat-related state (e.g. on logout). */
const resetChatState = () => {
  $isLoading.set(false);
  $input.set('');
  $customer.set(null);
  $messages.set([]);
  $settings.set({});
  $chats.set([]);
  $activeChatId.set(null);
  $loadingChat.set(false);
};

// Reset chat state reactively when the active customer's auth is removed
// (logout). Keeps auth.ts decoupled from the chat stores.
$customerAuth.subscribe((authMap) => {
  const shortID = getShortID();
  if (shortID && !authMap?.[shortID]?.accessToken) {
    resetChatState();
  }
});

onMount($messages, () => {
  task(async () => {
    const shortID = getShortID();
    if (!shortID) {
      return;
    }
    const customer = await getCustomer(shortID);
    $customer.set(customer);

    const settings = await getSettings(shortID);
    $settings.set(settings);

    // Open a fresh chat (greeting); prior chats live in the sidebar. The new
    // chat is created on the backend once the first message is sent.
    $activeChatId.set(null);
    $messages.set(processHistory([], customer));

    // Populate the sidebar list (best-effort; failure just means no history).
    loadChatList(shortID).catch((e) => console.warn('loadChatList failed', e));

    // Signal the native wrapper that the chat view is ready (was tied to the
    // old history load).
    if ($inRNWebView.get()) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ message: 'history loaded' }));
    }

    if ($initialMessage.get()) {
      chatEngine.sendMessage($initialMessage.get()!);
    }
  });
});

export const append = (message: Message) => setMessages((m) => m.concat([message]));
export const updateLastMessage = (
  role: string,
  text: string,
  run_id?: string,
  documents?: Document[],
  deeplinks?: Deeplink[],
) =>
  setMessages((messages) => {
    const lastMessage: Message = messages.at(-1) ?? {
      id: String(Date.now()),
      run_id,
      role,
      content: text,
      createdAt: new Date(),
      documents,
      deeplinks,
    };
    lastMessage.run_id = run_id;
    lastMessage.role = role;
    lastMessage.content = text;
    lastMessage.documents = documents;
    lastMessage.deeplinks = deeplinks;

    return [...messages.slice(0, -1), lastMessage];
  });

export const setMessages = (fnOrArray: ((messages: Message[]) => Message[]) | Message[]) => {
  if (Array.isArray(fnOrArray)) {
    $messages.set(fnOrArray);
    return;
  }
  $messages.set(fnOrArray($messages.get()));
};

export const setInput = (v: string) => $input.set(v);

export const handleAppend = (message: { role: 'user'; content: string }) => append(message as Message);
